import React from 'react';
import type { TFunction } from 'i18next';

import { Grid } from '../../../common/components/Grid/Grid';
import {
  GridColumn,
  GridCountFunction,
  GridGetterFunction,
} from '../../../common/components/Grid/GridDataTypes';
import { TKeys, useTranslation } from '../../../common/hooks/useTranslation';
import momencik from '../../../common/momencik';
import { GetEodLogDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import { EodDocumentStatusEnum } from '../../../common/pokCore/validation/enums';

import { EODRefreshErrorFromEodLink } from './EODRefreshErrorFromEodLink';

interface EODLogSearchProps {
  getData: GridGetterFunction;
  getCount: GridCountFunction;
  refresh: boolean;
}

export const eodLogForGrid = (
  t: TFunction<'fvzLng', 'fvzLng'>,
  tk: TKeys<'fvzLng'>,
  importErrors: GetEodLogDto,
  refresh: boolean,
  setRefresh: (refresh: boolean) => void,
) => {
  const isFromPok = importErrors.document?.isInvoiceFromPOK === 1;
  const isEodImportError =
    importErrors.document?.status ===
    EodDocumentStatusEnum.EodImportError.toString();
  const showRefresh = isFromPok && isEodImportError;

  return {
    key: importErrors.id.toString(),
    values: {
      'document.externalNumber': importErrors.document?.externalNumber,
      'error.date': momencik(importErrors.date),
      'error.type': importErrors.type,
      'error.user': importErrors.user,
      'error.eventName': importErrors.eventName,
      'error.details': importErrors.details,
      'error.refresh':
        showRefresh && importErrors.document?.id ? (
          <EODRefreshErrorFromEodLink
            eodId={importErrors.document?.id}
            onRefresh={() => setRefresh(!refresh)}
          />
        ) : (
          ''
        ),
    },
  };
};

export const EODLogSearch: React.FC<EODLogSearchProps> = props => {
  const { t, tk } = useTranslation('fvzLng');

  const columns: GridColumn[] = [
    {
      header: t(tk.eod.externalNumber),
      property: 'document.externalNumber',
    },
    {
      header: t(tk.common.modificationDate),
      property: 'error.date',
    },
    {
      header: t(tk.eod.errorType),
      property: 'error.type',
    },
    { header: t(tk.common.user), property: 'error.user' },
    { header: t(tk.eod.eventName), property: 'error.eventName' },
    { header: t(tk.eod.details), property: 'error.details' },
    {
      header: '',
      property: 'error.refresh',
      noSorting: true,
      noFilter: true,
    },
  ];

  return (
    <Grid
      getData={props.getData}
      getDataCount={props.getCount}
      columns={columns}
      showFilter={true}
      initialOrderColumn="error.date"
      initialOrderDirection="DESC"
      availablePageSizes={[10, 20, 50]}
      emptyGridMessage={t(tk.eod.noImportErrors)}
      withHistory={true}
      refresh={props.refresh}
    />
  );
};
